/**
 * Central mapping rules for clickable table cells in DISGENET.
 * Determines which table(s) open when a user clicks a value (Summary, Evidence, or both).
 *
 * - evidenceOnly: Only the Evidence table is shown (e.g. N PMIDs, N CTs, N PMIDs Chemicals).
 * - summaryAndEvidence: Both Summary and Evidence tables are shown (e.g. GDA, VDA, N Chemicals, N variants).
 * - diseaseBased: Value represents a disease; navigation/search uses the clicked disease (links or mapping with disease in context).
 */

export type MappingType = 'evidenceOnly' | 'summaryAndEvidence' | 'diseaseBased';

/** Column names that must open ONLY the Evidence table (no Summary). */
export const evidenceOnlyColumnNames: string[] = [
  'numPMIDsupportingAssociation', // N PMIDs (GDA/VDA)
  'numCTs', // N CTs (clinical trials count in GDA)
  'numberPmidsWithChemsIncludedInEvidence', // N PMIDs Chemicals
  'numberNCTSWithChemsIncludedInEvidence', // N CTs Chemicals
  'chemNumPublicationsGDA', // N PMIDs this GDA
  'chemNumPublicationsVDA', // N PMIDs this VDA
];

/** Column names that open Summary + Evidence. All mapping columns not in evidenceOnly default to this. */
export const summaryAndEvidenceColumnNames: string[] = [
  'numDBSNPsupportingAssociation', // N variants_gda
  'numberChemsIncludedInEvidence', // N Chemicals (in evidence)
  'numChemicals', // N Chemicals
  'ngenes_diseaseID_1',
  'ngenes_diseaseID_2',
  'nvariants_diseaseID_1',
  'nvariants_diseaseID_2',
  'nvariants_diseaseID_shared',
  'ngenes_diseaseID_shared',
];

/** Column names where the clicked value is disease-centric (search/map by disease). Used for consistency and future behavior. */
export const diseaseBasedColumnNames: string[] = [
  'ngenes_diseaseID_1',
  'ngenes_diseaseID_2',
  'nvariants_diseaseID_1',
  'nvariants_diseaseID_2',
  'nvariants_diseaseID_shared',
  'ngenes_diseaseID_shared',
];

const evidenceOnlySet = new Set(evidenceOnlyColumnNames);

/**
 * Returns the mapping type for a column name.
 * - evidenceOnly: show only Evidence table
 * - summaryAndEvidence: show Summary + Evidence tables
 * - diseaseBased: same as summaryAndEvidence but with disease context (handled by mappingItems)
 */
export function getMappingType(columnName: string | undefined): MappingType {
  if (!columnName) return 'summaryAndEvidence';
  return evidenceOnlySet.has(columnName) ? 'evidenceOnly' : 'summaryAndEvidence';
}

/** True if this column should show only the Evidence table (no Summary). */
export function isEvidenceOnlyMapping(columnName: string | undefined): boolean {
  return getMappingType(columnName) === 'evidenceOnly';
}

/** True if this column should show both Summary and Evidence tables. */
export function isSummaryAndEvidenceMapping(columnName: string | undefined): boolean {
  return getMappingType(columnName) === 'summaryAndEvidence';
}
